import { FC, Fragment, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { Helmet } from 'react-helmet-async';
import PageTitleWrapper from '../../../components/PageTitleWrapper';
import SitesStockSummaryHeader from './PageHeader';
import PageContainerWrapper from '../../../components/PageContainerWrapper';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, Link, TableContainer, useTheme, Box } from '@mui/material';
import { tableStyles } from '../Reports';
import { getSitesStockSummaryDataAction, getSitesStockSummaryResetAction } from './actions';
import { showAlertAction } from '../../../store/alerts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDisplayDateTime, formatNumber, currencyFormatter } from '../../../../src/utils';
import { useSearchParams } from 'react-router-dom'; // at the top
import { InlineEditableField } from '../../../components';


export type SitesStockSummaryProps = Record<string, never>

const SHOW_COLUMNS = false;

// Group data by party name
const groupByParty = (data: any[]): Array<{partyName: string, items: any[]}> => {
  if (!data) return []
  
  const grouped = data.reduce((acc, item) => {
    const partyName = item.party
    if (partyName === 'SUM') {
      // Find the previous party and add SUM to it
      const lastPartyKey = Object.keys(acc).pop()
      if (lastPartyKey && lastPartyKey !== 'SUM') {
        acc[lastPartyKey].push(item)
      } else {
        // If no previous party, create a SUM group
        if (!acc['SUM']) {
          acc['SUM'] = []
        }
        acc['SUM'].push(item)
      }
    } else {
      if (!acc[partyName]) {
        acc[partyName] = []
      }
      acc[partyName].push(item)
    }
    return acc
  }, {} as Record<string, any[]>)
  
  return Object.entries(grouped).map(([partyName, items]) => ({
    partyName,
    items: items as any[]
  }))
}

export const SitesStockSummary: FC<SitesStockSummaryProps> = ({}) => {
  const classes = tableStyles();
  const theme = useTheme();
  const { message, success, data: siteReportRows } = useAppSelector(
    (state) => state.sitesStockSummaryState
  );
  const dispatchAction = useAppDispatch();
  const [searchString, setSearchString] = useState('');
  const [filteredRows, setFilteredRows] = useState(siteReportRows);
  const [searchParams] = useSearchParams();
  const partyId = searchParams.get('party_id');

  useEffect(() => {
    dispatchAction(getSitesStockSummaryResetAction());
    dispatchAction(getSitesStockSummaryDataAction(partyId)); // Assuming your action supports param
  }, [dispatchAction, partyId]);

  useEffect(() => {
    if (!success && message) {
      dispatchAction(
        showAlertAction({
          message: message,
          severity: 'error',
        })
      );
    }
  }, [siteReportRows, message, success]);

  useEffect(() => {
    const normalizedSearch = searchString.trim().toLowerCase();
    const isExactMatch = searchString.endsWith('  ');

    if (normalizedSearch.length > 0 && siteReportRows) {
      const filtered = siteReportRows.filter((bill) => {
        const searchTarget = `${bill.party} ${bill.siteAddress} ${bill.siteNo} ${bill.itemName}`.toLowerCase();
        return isExactMatch
          ? ( bill.party?.toLowerCase() === normalizedSearch || bill.siteAddress?.toLowerCase() === normalizedSearch || `${bill.siteNo}`.toLowerCase() === normalizedSearch || bill.itemName?.toLowerCase() === normalizedSearch)
          : searchTarget.includes(normalizedSearch) && bill.party !== 'SUM';
      });
      setFilteredRows(filtered);
    } else {
      setFilteredRows(siteReportRows);
    }
  }, [searchString, siteReportRows]);

  const exportTableToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = SHOW_COLUMNS
      ? ['S.N.', 'Site (ID)', 'Item', 'Issued', 'Received', 'Lost', 'Balance', 'Pc(Kg)', 'Pc(INR)', 'Weight', 'Value']
      : ['S.N.', 'Site (ID)', 'Item', 'Issued', 'Received', 'Lost', 'Balance', 'Weight', 'Value'];
    const tableRows: any[] = [];
    let issued = 0;
    let received = 0;
    let lost = 0;
    let balance = 0;
    let pcWeight = 0;
    let pcValue = 0;
    let weight = 0;
    let value = 0;
    let count = 0;
  
    filteredRows.forEach((bill) => {
      const isNotSum = bill.party !== 'SUM';
      issued += isNotSum ? bill.issuedQuantity : 0;
      received += isNotSum ? bill.receivedQuantity : 0;
      lost += isNotSum ? (bill.lostQuantity || 0) : 0;
      balance += isNotSum ? bill.balanceQuantity : 0;
      pcWeight += isNotSum ? (bill.pcWeight || 0) : 0;
      pcValue += isNotSum ? (bill.pcValue || 0) : 0;
      weight += isNotSum ? (bill.weight || 0) : 0;
      value += isNotSum ? (bill.value || 0) : 0;
  
      const siteDisplay = isNotSum && bill.siteAddress && bill.siteNo 
        ? `${bill.siteAddress} (${bill.siteNo})`
        : (isNotSum ? (bill.siteAddress || bill.siteNo || '') : 'Sum');
  
      const rowData = SHOW_COLUMNS
        ? [
            isNotSum ? ++count : '',
            siteDisplay,
            isNotSum ? bill.itemName : '',
            bill.issuedQuantity,
            bill.receivedQuantity,
            bill.lostQuantity || 0,
            bill.balanceQuantity,
            bill.pcWeight || 0,
            bill.pcValue || 0,
            bill.weight || 0,
            bill.value || 0,
          ]
        : [
            isNotSum ? ++count : '',
            siteDisplay,
            isNotSum ? bill.itemName : '',
            bill.issuedQuantity,
            bill.receivedQuantity,
            bill.lostQuantity || 0,
            bill.balanceQuantity,
            bill.weight || 0,
            bill.value || 0,
          ];
      tableRows.push(rowData);
    });
  
    if (tableRows.length > 0) {
      const grandSumRow = SHOW_COLUMNS
        ? [
            '',
            'Grand Sum',
            '',
            issued,
            received,
            lost,
            balance,
            pcWeight,
            pcValue,
            weight,
            value,
          ]
        : [
            '',
            'Grand Sum',
            '',
            issued,
            received,
            lost,
            balance,
            weight,
            value,
          ];
      tableRows.push(grandSumRow);
    }

    doc.text('Balance Items '+(getDisplayDateTime(new Date())), 14, 14);
    

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'grid',
      didParseCell: (data) => {
        const isGrandSumRow = data.row.raw[1] === 'Grand Sum' || data.row.raw[1] === 'Sum';
        if (isGrandSumRow) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = '#000000'; // Optional: Change text color
        }
      },
    });
  
    doc.save('site-stock-summary.pdf');
  };
  
  

  let count = 0;
  const groupedData: Array<{partyName: string, items: any[]}> = groupByParty(filteredRows || []);
  
  return (
    <>
      <Helmet>
        <title>Billing Report</title>
      </Helmet>
      <PageTitleWrapper>
        <SitesStockSummaryHeader
          searchString={searchString}
          setSearchString={setSearchString}
          partyId={partyId}
          onPdfClick={exportTableToPDF}
        />
      </PageTitleWrapper>
      <PageContainerWrapper>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
          <Table aria-label="billing report" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    borderLeft: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '5%'
                  }}
                >
                  S.N.
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '20%'
                  }}
                >
                  Site (ID)
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '13%'
                  }}
                >
                  Item
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '8.29%'
                  }}
                >
                  Issued
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '8.29%'
                  }}
                >
                  Received
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '8.29%'
                  }}
                >
                  Lost
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '8.29%'
                  }}
                >
                  Balance
                </TableCell>
                {SHOW_COLUMNS && (
                  <TableCell 
                    align="right"
                    sx={{ 
                      backgroundColor: theme.palette.primary.main,
                      borderRight: '0.5px solid #ccc',
                      fontWeight: 600,
                      color: theme.palette.primary.contrastText,
                      width: '9.29%',
                      paddingRight: 1
                    }}
                  >
                    Kg/Pc
                  </TableCell>
                )}
                {SHOW_COLUMNS && (
                  <TableCell 
                    align="right"
                    sx={{ 
                      backgroundColor: theme.palette.primary.main,
                      borderRight: '0.5px solid #ccc',
                      fontWeight: 600,
                      color: theme.palette.primary.contrastText,
                      width: '9.29%',
                      paddingRight: 1
                    }}
                  >
                    Price/Pc
                  </TableCell>
                )}
                <TableCell 
                  align="right"
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '9.29%'
                  }}
                >
                  Weight
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    width: '9.29%'
                  }}
                >
                  Value
                </TableCell>
              </TableRow>
            </TableHead>
          <TableBody>
            {              groupedData?.map((group) => {
                return (
                  <Fragment key={group.partyName}>
                    {/* Party Header Row */}
                    <TableRow 
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        height: '48px',
                        minHeight: '48px',
                        maxHeight: '48px',
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: 600,
                          fontSize: '1rem'
                        }
                      }}
                    >
                      <TableCell colSpan={SHOW_COLUMNS ? 11 : 9} sx={{ padding: '12px 16px' }}>
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        >
                          {!partyId ? (
                            <a
                              href={`${window.location.pathname}?party_id=${group.items[0]?.partyId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#1976d2',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '1rem'
                              }}
                            >
                              {group.partyName}
                            </a>
                          ) : (
                            group.partyName
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    {/* Site Rows for this Party */}
                    {group.items.map((bill, index) => {
                      const isNotSum = !(bill.party === 'SUM');
                      return (
                        <TableRow 
                          key={`${group.partyName}-${index}`}
                          sx={{
                            height: '48px',
                            minHeight: '48px',
                            maxHeight: '48px',
                            ...(bill.party === 'SUM' ? { 
                              fontWeight: 'bold',
                              '& .MuiTableCell-root': {
                                borderTop: '1px solid #ccc'
                              }
                            } : {})
                          }}
                        >
                          <TableCell className={classes.tableCell} sx={{ width: '5%' }}>{isNotSum ? ++count : ''}</TableCell>
                          {isNotSum ? (
                            <>
                              <TableCell className={classes.tableCell} sx={{ width: '20%' }}>
                                {bill.siteNo ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                    <Link
                                      color={'#0000ff'}
                                      underline="always"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      href={`/slips/view-slips/${bill.siteNo}/${bill.partyId}?tab_index=3`}
                                      sx={{ flexShrink: 0 }}
                                    >
                                      {bill.siteNo}
                                    </Link>
                                    {bill.siteAddress && (
                                      <Typography
                                        component="span"
                                        sx={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          fontSize: '0.875rem',
                                          color: 'text.secondary'
                                        }}
                                      >
                                        {bill.siteAddress}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  bill.siteAddress || ''
                                )}
                              </TableCell>
                              <TableCell className={classes.tableCell} sx={{ width: '10%' }}>{bill.itemName}</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell colSpan={2} className={classes.tableCell} sx={{ width: '30%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Total</Typography>
                              </TableCell>
                            </>
                          )}
                          <TableCell className={classes.tableCell} sx={{ width: '9.29%' }}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {bill.issuedQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell} sx={{ width: '9.29%' }}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {bill.receivedQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell} sx={{ width: '9.29%' }}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {bill.lostQuantity || 0}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell} sx={{ width: '9.29%' }}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {bill.balanceQuantity}
                            </Typography>
                          </TableCell>
                          {SHOW_COLUMNS && (
                            <TableCell className={classes.tableCell} align="right" sx={{ width: '9.29%', paddingRight: isNotSum ? 0 : '28px' }}>
                              {isNotSum ? (
                                <InlineEditableField
                                  value={bill.pcWeight ?? 0}
                                  fieldName="weight"
                                  entryId={bill.lastEntryId}
                                  entity="entry"
                                 
                                  parser={(val) => parseFloat(val.replace(/[^0-9.]/g, ''))}
                                  type="number"
                                  min={0}
                                  onUpdateSuccess={() => dispatchAction(getSitesStockSummaryDataAction(partyId || undefined))}
                                />
                              ) : (
                                ''
                              )}
                            </TableCell>
                          )}
                          {SHOW_COLUMNS && (
                            <TableCell className={classes.tableCell} align="right" sx={{ width: '9.29%', paddingRight: isNotSum ? 0 : '28px' }}>
                              {isNotSum ? (
                                <InlineEditableField
                                  value={bill.pcValue ?? 0}
                                  fieldName="value"
                                  entryId={bill.lastEntryId}
                                  entity="entry"
                                  formatter={currencyFormatter.format}
                                  parser={(val) => parseFloat(val.replace(/[^0-9.]/g, ''))}
                                  type="number"
                                  min={0}
                                  onUpdateSuccess={() => dispatchAction(getSitesStockSummaryDataAction(partyId || undefined))}
                                />
                              ) : (
                                ''
                              )}
                            </TableCell>
                          )}
                          <TableCell className={classes.tableCell} align="right" sx={{ width: '9.29%' }}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {formatNumber(bill.weight || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right" sx={{ width: '9.29%' }}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {formatNumber(bill.value || 0)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                );
              })}
          </TableBody>
        </Table>
        </TableContainer>
      </PageContainerWrapper>
    </>
  );
};

export default SitesStockSummary;
